import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import { SemanaMonitoreo } from "../../../../../modulos/monitoreo/sesion/agruparPorSemana";
import { leerResultado } from "../../../../../modulos/monitoreo/sesion/response/SesionDashboardResponse";
import {
  ESTADO_RANK,
  evaluarEnLote,
  MonitoreoEstado,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import {
  calcularProyeccion,
  Proyeccion,
} from "./monitoreoAnalisisProyeccion";
import { formatPorcentaje } from "../../../../../modulos/monitoreo/sesion/formatIncidencia";

/** Variación de una combinación lote × plaga entre las dos últimas sesiones
 *  que la reportaron. */
export type Tendencia = {
  /** Nombre del lote, para mostrar. */
  lote: string;
  /** Plaga evaluada. */
  plaga: MonitoreoPlagaEnfermedadItem;
  /** Valor en la última sesión con dato. */
  valorUltimo: number;
  /** Valor en la sesión anterior con dato. */
  valorPrevio: number;
  /** Diferencia (positiva = la presión subió). */
  delta: number;
  /** Estado del semáforo en la última sesión. */
  estado: MonitoreoEstado;
  /** La combinación lleva dos sesiones seguidas en alerta. */
  sostenida: boolean;
  /** Semana ISO de la última sesión con dato. */
  semanaUltima: number;
  /** Semana ISO de la sesión previa con dato. */
  semanaPrevia: number;
  /** Umbral de alerta de incidencia con el que se evaluó la última lectura, o
   *  `null` si ese organismo no tiene uno (crítico, o sin valores cargados).
   *  Va en la tarjeta y lo usa la proyección. */
  umbralAlerta: number | null;
  /** Estimación de cuánto falta para cruzar el umbral de alerta, o `null`
   *  cuando no hay base para afirmarla (ver `calcularProyeccion`). */
  proyeccion: Proyeccion | null;
};

/** Cuántas tarjetas se muestran (las más severas primero). Más allá de esto la
 *  franja deja de leerse como resumen. */
const MAX_TARJETAS = 7;

/**
 * Calcula la variación de cada combinación lote × plaga activa.
 *
 * Compara las dos últimas semanas **en las que ese lote fue monitoreado**, que
 * no son necesariamente las dos últimas del histórico: un lote puede no
 * haberse recorrido en la semana más reciente, y compararlo contra una semana
 * donde no fue medido daría un salto falso.
 *
 * Una lectura de **cero cuenta como lectura**: que una plaga haya desaparecido
 * es justamente la variación que interesa reportar.
 */
export function computeTendencias(
  semanas: SemanaMonitoreo[],
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
  activeLotes: string[],
  activePlagas: string[],
  loteNombres: Map<string, string>,
): Tendencia[] {
  const tendencias: Tendencia[] = [];

  for (const loteId of activeLotes) {
    for (const plagaId of activePlagas) {
      const plaga = plagasById.get(plagaId);
      if (!plaga) continue;

      // La serie es de **incidencia** (% de árboles), que es la métrica del
      // análisis histórico (supuesto S5). Cada semana se evalúa con la
      // referencia congelada de su propia sesión, no con los umbrales de hoy.
      const lecturas = semanas
        .map((semana) => {
          const lote = leerResultado(semana, loteId);
          const evaluacion = lote
            ? evaluarEnLote(lote, plagaId, plaga)
            : null;
          return {
            semana: semana.semana,
            fecha: semana.fecha,
            valor: evaluacion?.incidencia ?? null,
            estado: evaluacion?.estado ?? null,
            umbralAlerta: lote?.referencias[plagaId]?.incidenciaMedioMax ?? null,
          };
        })
        .filter(
          (
            lectura,
          ): lectura is {
            semana: number;
            fecha: string;
            valor: number;
            estado: MonitoreoEstado | null;
            umbralAlerta: number | null;
          } => lectura.valor !== null,
        );
      if (lecturas.length < 2) continue;

      const ultima = lecturas[lecturas.length - 1];
      const previa = lecturas[lecturas.length - 2];

      // Sin estado no hay tarjeta: es un benéfico o un organismo sin valores de
      // referencia, y una tarjeta de tendencia sin estado no dice nada
      // accionable -mostraría "En control" sobre algo que no se evaluó-.
      if (ultima.estado === null) continue;

      // "Presión sostenida" = dos lecturas seguidas en alerta. Sale del estado
      // ya evaluado y no de comparar contra el umbral a mano, para que también
      // aplique a los organismos críticos, donde cualquier detección es alerta y
      // el umbral puede no existir.
      const estado = ultima.estado;
      const sostenida = estado === "alert" && previa.estado === "alert";

      tendencias.push({
        lote: loteNombres.get(loteId) ?? loteId,
        plaga,
        valorUltimo: ultima.valor,
        valorPrevio: previa.valor,
        delta: ultima.valor - previa.valor,
        estado,
        sostenida,
        semanaUltima: ultima.semana,
        semanaPrevia: previa.semana,
        umbralAlerta: ultima.umbralAlerta,
        proyeccion: calcularProyeccion(lecturas, ultima.umbralAlerta),
      });
    }
  }

  // Orden: sostenidas primero, luego por severidad, y a igual severidad la
  // variación más grande en valor absoluto (sube o baja, ambas importan).
  tendencias.sort((a, b) => {
    if (a.sostenida !== b.sostenida) return a.sostenida ? -1 : 1;
    const severidad = ESTADO_RANK[b.estado] - ESTADO_RANK[a.estado];
    if (severidad !== 0) return severidad;
    return Math.abs(b.delta) - Math.abs(a.delta);
  });

  return tendencias.slice(0, MAX_TARJETAS);
}

/** HTML de la franja de tarjetas. El estado va siempre con etiqueta de texto,
 *  nunca solo por color. */
export function renderTendenciasHtml(tendencias: Tendencia[]): string {
  if (tendencias.length === 0) {
    return `<div class="sx-empty">Todavía no hay dos sesiones con datos para comparar.</div>`;
  }
  return tendencias.map(_renderTarjeta).join("");
}

/** Una tarjeta de tendencia. */
function _renderTarjeta(tendencia: Tendencia): string {
  const subiendo = tendencia.delta > 0;
  const modificador = tendencia.sostenida
    ? "mon-trend--sostenida"
    : `mon-trend--${tendencia.estado}`;
  const etiqueta = tendencia.sostenida
    ? "Presión sostenida"
    : tendencia.estado === "alert"
      ? "Alerta"
      : tendencia.estado === "warn"
        ? "Precaución"
        : "En control";
  const umbral =
    tendencia.umbralAlerta !== null && tendencia.umbralAlerta > 0
      ? formatPorcentaje(tendencia.umbralAlerta)
      : "—";
  // Sin variación no hay flecha que mostrar: un "↑ 0.0" se lee como que subió.
  const variacion =
    tendencia.delta === 0
      ? `<span class="mon-trend-delta mon-trend-delta--igual">sin cambio</span>`
      : `<span class="mon-trend-delta mon-trend-delta--${subiendo ? "up" : "down"}">${subiendo ? "↑" : "↓"} ${formatPorcentaje(Math.abs(tendencia.delta))}</span>`;

  // La proyección se muestra solo cuando el cálculo la sostiene, y siempre
  // condicionada ("al ritmo actual"): es una extrapolación, no una medición.
  const proyeccion = tendencia.proyeccion
    ? `<div class="mon-trend-proyeccion">Cruza el umbral en <strong>~${tendencia.proyeccion.plazo}</strong> al ritmo actual</div>`
    : "";

  return `<div class="mon-trend ${modificador}">
      <div class="mon-trend-label">${etiqueta}</div>
      <div class="mon-trend-main">${tendencia.lote} · ${tendencia.plaga.nombreComun}</div>
      <div class="mon-trend-row">
        ${variacion}
        <span class="mon-trend-sub">Sem ${tendencia.semanaPrevia} → ${tendencia.semanaUltima}</span>
      </div>
      <div class="mon-trend-sub">Último: <strong>${formatPorcentaje(tendencia.valorUltimo)}</strong> · umbral de alerta: ${umbral}</div>
      ${proyeccion}
    </div>`;
}
